/**
 * Bilingual field primitives for the Landing CMS.
 *
 * Every section editor uses one or more of these.  The pattern: each primitive
 * is a leaf that knows its own path and writes through `setField(lang, path,
 * value)` on the draft hook.  The section editor passes in `getField`,
 * `setField`, and the path; the primitive renders two inputs side-by-side
 * (one per language) with consistent styling.
 *
 * Why side-by-side instead of two tabs?  The whole point of the CMS is
 * editing both languages together — splitting them across tabs hides drift.
 * The shared-row layout makes "Arabic is missing here" visually obvious.
 *
 * Single-language variants exist too (TextField / TextareaField / UrlField)
 * for fields like logo URL that are language-agnostic.
 */

import { useId } from 'react'

const ROW_CLASSES = 'grid grid-cols-1 md:grid-cols-2 gap-3 mb-3'
const LABEL_CLASSES = 'text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1'
const INPUT_CLASSES =
  'w-full h-10 px-3 text-sm bg-white border border-gray-300 rounded-lg ' +
  'focus:outline-none focus:ring-2 focus:ring-cyan-500/30 focus:border-cyan-500 ' +
  'placeholder:text-gray-400 transition-colors'
const TEXTAREA_CLASSES =
  'w-full px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg ' +
  'focus:outline-none focus:ring-2 focus:ring-cyan-500/30 focus:border-cyan-500 ' +
  'placeholder:text-gray-400 transition-colors resize-y min-h-[80px]'

function FieldHeader({ label, htmlFor, hint }) {
  return (
    <div className="flex items-baseline justify-between">
      <label htmlFor={htmlFor} className={LABEL_CLASSES}>{label}</label>
      {hint && <span className="text-[10px] text-gray-400 font-normal normal-case">{hint}</span>}
    </div>
  )
}

/**
 * BilingualTextField — one row, two single-line inputs.
 * Props:
 *   - label              shown above both columns
 *   - path               dotted path in the draft, e.g. "hero.slides.0.title"
 *   - getField/setField  from useLandingDraft
 *   - placeholder        optional
 *   - hint               small grey text top-right of the label row
 */
export function BilingualTextField({ label, path, getField, setField, placeholder, hint }) {
  const idEn = useId()
  const idAr = useId()
  const enValue = getField('en', path, '')
  const arValue = getField('ar', path, '')
  const arEmpty = !arValue || arValue.trim() === ''

  return (
    <div className={ROW_CLASSES}>
      <div>
        <FieldHeader label={`${label} — EN`} htmlFor={idEn} hint={hint} />
        <input
          id={idEn}
          type="text"
          dir="auto"
          value={enValue}
          onChange={(e) => setField('en', path, e.target.value)}
          placeholder={placeholder}
          className={INPUT_CLASSES}
        />
      </div>
      <div>
        <FieldHeader
          label={`${label} — AR`}
          htmlFor={idAr}
          hint={arEmpty ? 'Not yet translated' : null}
        />
        <input
          id={idAr}
          type="text"
          dir="auto"
          value={arValue}
          onChange={(e) => setField('ar', path, e.target.value)}
          placeholder={placeholder}
          className={`${INPUT_CLASSES} ${arEmpty ? 'border-amber-300 bg-amber-50/30' : ''}`}
        />
      </div>
    </div>
  )
}

/**
 * BilingualTextareaField — one row, two multi-line textareas.
 */
export function BilingualTextareaField({ label, path, getField, setField, placeholder, rows = 3 }) {
  const idEn = useId()
  const idAr = useId()
  const enValue = getField('en', path, '')
  const arValue = getField('ar', path, '')
  const arEmpty = !arValue || arValue.trim() === ''

  return (
    <div className={ROW_CLASSES}>
      <div>
        <FieldHeader label={`${label} — EN`} htmlFor={idEn} />
        <textarea
          id={idEn}
          dir="auto"
          rows={rows}
          value={enValue}
          onChange={(e) => setField('en', path, e.target.value)}
          placeholder={placeholder}
          className={TEXTAREA_CLASSES}
        />
      </div>
      <div>
        <FieldHeader
          label={`${label} — AR`}
          htmlFor={idAr}
          hint={arEmpty ? 'Not yet translated' : null}
        />
        <textarea
          id={idAr}
          dir="auto"
          rows={rows}
          value={arValue}
          onChange={(e) => setField('ar', path, e.target.value)}
          placeholder={placeholder}
          className={`${TEXTAREA_CLASSES} ${arEmpty ? 'border-amber-300 bg-amber-50/30' : ''}`}
        />
      </div>
    </div>
  )
}

/**
 * UrlField — single-language input dedicated to URLs.  Used for footer
 * link hrefs (which are language-specific) AND for things like logo URLs
 * that are language-agnostic (in which case caller picks one lang).
 */
export function UrlField({ label, value, onChange, placeholder = 'https://...', hint }) {
  const id = useId()
  return (
    <div className="mb-3">
      <FieldHeader label={label} htmlFor={id} hint={hint} />
      <div className="flex items-center gap-2">
        <input
          id={id}
          type="url"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          dir="ltr"
          className={INPUT_CLASSES}
        />
        {value && (
          <a
            href={value}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs px-2 py-1 rounded border border-gray-300 text-gray-600 hover:bg-gray-50 whitespace-nowrap"
            title="Open in new tab"
          >
            Open
          </a>
        )}
      </div>
    </div>
  )
}

/**
 * BilingualUrlField — pairs URLs where the destination differs by language
 * (e.g. footer "Terms" might link to /terms vs /ar/terms).  Most footer
 * links share the same href across languages — caller decides which to use.
 */
export function BilingualUrlField({ label, path, getField, setField, placeholder = '/path or https://...' }) {
  const idEn = useId()
  const idAr = useId()
  return (
    <div className={ROW_CLASSES}>
      <div>
        <FieldHeader label={`${label} URL — EN`} htmlFor={idEn} />
        <input
          id={idEn}
          type="text"
          dir="ltr"
          value={getField('en', path, '')}
          onChange={(e) => setField('en', path, e.target.value)}
          placeholder={placeholder}
          className={INPUT_CLASSES}
        />
      </div>
      <div>
        <FieldHeader label={`${label} URL — AR`} htmlFor={idAr} />
        <input
          id={idAr}
          type="text"
          dir="ltr"
          value={getField('ar', path, '')}
          onChange={(e) => setField('ar', path, e.target.value)}
          placeholder={placeholder}
          className={INPUT_CLASSES}
        />
      </div>
    </div>
  )
}

/**
 * MediaUrlField — image or video reference.  v1 just edits the URL field
 * directly (and shows a thumbnail preview if the URL is an image).
 * A proper media picker / uploader is planned for v2.
 */
export function MediaUrlField({ label, value, onChange, kind = 'image', hint }) {
  const id = useId()
  return (
    <div className="mb-3">
      <FieldHeader label={label} htmlFor={id} hint={hint} />
      <div className="flex items-start gap-3">
        <div className="flex-1 space-y-2">
          <input
            id={id}
            type="text"
            dir="ltr"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={kind === 'video' ? 'https://...mp4 or /assets/...' : 'https://...png or /assets/...'}
            className={INPUT_CLASSES}
          />
          <p className="text-[11px] text-gray-500">
            Paste a URL or relative path. v1 doesn't include an uploader — host the file elsewhere or use an existing path.
          </p>
        </div>
        {kind === 'image' && value && (
          // eslint-disable-next-line jsx-a11y/img-redundant-alt
          <img
            src={value}
            alt="preview"
            className="w-20 h-20 object-cover rounded-lg border border-gray-200 bg-gray-50"
            onError={(e) => { e.currentTarget.style.opacity = '0.3' }}
          />
        )}
        {kind === 'video' && value && (
          <video src={value} className="w-32 h-20 rounded-lg border border-gray-200 bg-gray-50" muted />
        )}
      </div>
    </div>
  )
}

/**
 * GradientPicker — pick from a preset list of brand-aligned hero gradients.
 * Stored as the same Tailwind utility string the LandingPage already uses
 * (so the public page renders it directly).
 */
const PRESET_GRADIENTS = [
  { label: 'Cyan → Mint',   value: 'from-[#29AECB] via-[#01DCBA] to-[#748f89]' },
  { label: 'Deep → Cyan',   value: 'from-[#2f584f] via-[#748f89] to-[#29AECB]' },
  { label: 'Slate → Mint',  value: 'from-[#748f89] via-[#29AECB] to-[#01DCBA]' },
  { label: 'Mint → Slate',  value: 'from-[#01DCBA] via-[#29AECB] to-[#748f89]' },
  { label: 'Ocean',         value: 'from-[#0ea5e9] via-[#0891b2] to-[#06b6d4]' },
  { label: 'Sunset',        value: 'from-[#f97316] via-[#ef4444] to-[#a855f7]' },
  { label: 'Forest',        value: 'from-[#10b981] via-[#059669] to-[#047857]' },
  { label: 'Royal',         value: 'from-[#6366f1] via-[#8b5cf6] to-[#a855f7]' },
]

export function GradientPicker({ label, value, onChange }) {
  return (
    <div className="mb-3">
      <span className={LABEL_CLASSES}>{label}</span>
      <div className="grid grid-cols-4 sm:grid-cols-8 gap-2 mt-1">
        {PRESET_GRADIENTS.map((preset) => {
          const selected = value === preset.value
          return (
            <button
              key={preset.value}
              type="button"
              onClick={() => onChange(preset.value)}
              className={`h-12 rounded-lg bg-gradient-to-br ${preset.value} relative transition-all ${
                selected ? 'ring-2 ring-cyan-500 ring-offset-2 scale-95' : 'hover:scale-105'
              }`}
              title={preset.label}
            >
              {selected && (
                <span className="absolute inset-0 flex items-center justify-center text-white text-sm font-bold drop-shadow">
                  ✓
                </span>
              )}
            </button>
          )
        })}
      </div>
    </div>
  )
}

/**
 * SectionPanel — collapsible card wrapping a section editor.
 */
export function SectionPanel({ title, subtitle, defaultOpen = true, children }) {
  return (
    <details
      open={defaultOpen}
      className="group bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
    >
      <summary className="cursor-pointer select-none px-5 py-4 hover:bg-gray-50 transition-colors flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold text-gray-900">{title}</h3>
          {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
        </div>
        <span className="text-gray-400 text-sm group-open:rotate-180 transition-transform">▾</span>
      </summary>
      <div className="px-5 pb-5 pt-1 border-t border-gray-100">{children}</div>
    </details>
  )
}

/**
 * ItemListEditor — generic add/remove/reorder for arrays of bilingual items
 * (testimonials, FAQ entries, footer links, etc.).
 *
 * Props:
 *   - title            shown above the list
 *   - itemsEn / itemsAr arrays from each language draft (must match length)
 *   - onChange(en, ar)  caller updates both arrays
 *   - makeEmpty        () => empty item shape for "Add new"
 *   - renderItem(idx, item) JSX for one row's editors (called for whichever
 *                      language is the "primary" — but typically callers
 *                      build their own bilingual rows inside)
 */
export function ItemListEditor({ title, itemsEn = [], itemsAr = [], onChange, makeEmpty, children, addLabel = 'Add item', minItems = 0 }) {
  const len = Math.max(itemsEn.length, itemsAr.length)

  const remove = (idx) => {
    if (len <= minItems) return
    const en = itemsEn.slice(); en.splice(idx, 1)
    const ar = itemsAr.slice(); ar.splice(idx, 1)
    onChange(en, ar)
  }
  const moveUp = (idx) => {
    if (idx === 0) return
    const en = itemsEn.slice(); [en[idx - 1], en[idx]] = [en[idx], en[idx - 1]]
    const ar = itemsAr.slice(); [ar[idx - 1], ar[idx]] = [ar[idx], ar[idx - 1]]
    onChange(en, ar)
  }
  const moveDown = (idx) => {
    if (idx >= len - 1) return
    const en = itemsEn.slice(); [en[idx + 1], en[idx]] = [en[idx], en[idx + 1]]
    const ar = itemsAr.slice(); [ar[idx + 1], ar[idx]] = [ar[idx], ar[idx + 1]]
    onChange(en, ar)
  }
  const add = () => {
    const empty = makeEmpty()
    const en = [...itemsEn, empty.en ?? empty]
    const ar = [...itemsAr, empty.ar ?? empty]
    onChange(en, ar)
  }

  return (
    <div className="space-y-3">
      {title && <h4 className="text-sm font-semibold text-gray-800">{title}</h4>}
      {children({ count: len, moveUp, moveDown, remove })}
      <button
        type="button"
        onClick={add}
        className="text-sm px-3 py-2 rounded-lg border border-dashed border-gray-300 text-gray-600 hover:bg-gray-50 hover:border-cyan-400 hover:text-cyan-700 transition-colors"
      >
        + {addLabel}
      </button>
    </div>
  )
}
